import { forwardRef } from 'react';

import {
  Column,
  ColumnGroup,
  Drawer,
  TableHeader,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Typography,
  Stack,
} from '@cast/design-system';

import { tableCacheKeys } from 'constants/tableCacheKeys';
import { WorkloadResourceRequestCell } from 'features/cluster/nodes/_components/WorkloadResourceRequestCell';

import { WorkloadBaseRunningInNode } from '../../../types';

import {
  NoData,
  NoResults,
  Search,
  SearchableTable,
  SearchableTableRecordsCount,
  SearchProvider,
} from '@cast/shared-search-ui';

type NodeWorkloadsSubDrawerProps = {
  workloads: WorkloadBaseRunningInNode[];
};

export const NodeWorkloadsSubDrawer = forwardRef<
  HTMLDivElement,
  NodeWorkloadsSubDrawerProps
>(({ workloads, ...props }: NodeWorkloadsSubDrawerProps, ref) => {
  return (
    <Drawer ref={ref} {...props} size="medium" testId="node-workloads-drawer">
      <SearchProvider urlKey="node-workloads-filter" data={workloads}>
        <DrawerHeader />
        <DrawerBody height="100%">
          <DrawerContent>
            <Stack mt={32} mb={18} gap={16}>
              <Typography variant="h5">Workloads running</Typography>
              <Search testId="node-workloads-search" />
            </Stack>
          </DrawerContent>
          <DrawerContent px={0} height="100%">
            <SearchableTable
              cacheKey={tableCacheKeys.NODES.WORKLOADS}
              components={{
                noData: <NoData entity="workload" />,
                noResults: <NoResults entity="workload" />,
              }}
              outerHeader={
                <TableHeader
                  recordsCount={
                    <SearchableTableRecordsCount title="workload" />
                  }
                  px={32}
                />
              }
              fillParentHeight
              testId="node-workloads-table"
            >
              <Column
                id="name"
                minWidth={160}
                width={160}
                header="Workload"
                cellWrapperSx={{ paddingLeft: '32px' }}
                bodyCellSx={{ typography: 'P12M' }}
                sortable
                resizable
              />

              <Column
                id="workloadType"
                width={165}
                header="Type"
                sortable
                defaultSort="desc"
              />

              <Column
                id="namespace"
                minWidth={140}
                header="namespace"
                sortable
              />

              <ColumnGroup id="resources-requested" title="Resources requested">
                <Column
                  id="requestedCpu"
                  width={100}
                  header="CPU"
                  renderCell={(workload: WorkloadBaseRunningInNode) => {
                    return (
                      <WorkloadResourceRequestCell
                        workload={workload}
                        resource="cpu"
                      />
                    );
                  }}
                  sortable
                  sortingType="number"
                  accessor="resourcesPerNode.requested.milliCpu"
                />

                <Column
                  id="requestedMemory"
                  width={120}
                  header="Memory"
                  renderCell={(workload: WorkloadBaseRunningInNode) => {
                    return (
                      <WorkloadResourceRequestCell
                        workload={workload}
                        resource="memory"
                      />
                    );
                  }}
                  sortable
                  sortingType="number"
                  accessor="resourcesPerNode.requested.memoryBytes"
                />
              </ColumnGroup>
            </SearchableTable>
          </DrawerContent>
        </DrawerBody>
      </SearchProvider>
    </Drawer>
  );
});

NodeWorkloadsSubDrawer.displayName = 'NodeWorkloadsSubDrawer';
